import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { getPublicMediaUrl } from "../lib/mediaUrl";

export default function ArticleCard({ article, featured = false }) {
  const firstPhoto = article.photos?.[0];
  const imageUrl = getPublicMediaUrl(firstPhoto) || firstPhoto?.thumbnail_url ||
    "https://images.pexels.com/photos/3601425/pexels-photo-3601425.jpeg?auto=compress&cs=tinysrgb&w=800";

  const formattedDate = format(new Date(article.created_at), "d MMMM yyyy", { locale: fr });

  if (featured) {
    return (
      <Link 
        to={`/article/${article.slug || article.id}`} 
        className="group relative block overflow-hidden rounded-2xl"
        data-testid={`article-card-${article.id}`}
      >
        <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden">
          <img
            src={imageUrl}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
            {article.category_name && (
              <span className="inline-block px-4 py-1.5 bg-accent text-white text-xs font-medium tracking-wider uppercase rounded-full mb-4">
                {article.category_name}
              </span>
            )}
            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              {article.title}
            </h2>
            {article.excerpt && (
              <p className="text-white/80 text-base md:text-lg max-w-2xl line-clamp-2 mb-6 font-body">
                {article.excerpt}
              </p>
            )}
            <div className="flex items-center gap-6 text-white/60 text-sm font-body">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {formattedDate}
              </span>
              {article.destination_name && (
                <span className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {article.destination_name}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link 
      to={`/article/${article.slug || article.id}`} 
      className="group block bg-white rounded-2xl overflow-hidden border border-border/30 hover:border-accent/30 hover:shadow-xl transition-all duration-300"
      data-testid={`article-card-${article.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={imageUrl}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {article.category_name && (
          <span className="absolute top-4 left-4 px-3 py-1.5 bg-white/95 backdrop-blur-sm text-primary text-xs font-medium tracking-wider uppercase rounded-full shadow-sm">
            {article.category_name}
          </span>
        )}
      </div>
      <div className="p-6 md:p-8">
        <div className="flex items-center gap-4 text-muted-foreground text-xs font-body mb-4">
          <span className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            {formattedDate}
          </span>
          {article.destination_name && (
            <span className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              {article.destination_name}
            </span>
          )}
        </div>
        <h3 className="font-heading text-xl md:text-2xl font-semibold text-primary group-hover:text-accent transition-colors line-clamp-2 leading-snug mb-3">
          {article.title}
        </h3>
        {article.excerpt && (
          <p className="text-muted-foreground line-clamp-2 text-sm font-body leading-relaxed mb-4">
            {article.excerpt}
          </p>
        )}
        <div className="flex items-center gap-2 text-accent text-sm font-medium font-body">
          Lire la suite
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
}
